import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";

const TILES_URL  = import.meta.env.VITE_TILES_URL;          // Martin/PostGIS path (unused in static deploy)
const PMTILES_URL = import.meta.env.VITE_PMTILES_URL;       // R2-hosted PMTiles (production path)

// Carto Dark Matter — free, no API key, includes street geometry + labels.
// Works under MapLibre + their attribution requirement.
const BASEMAP_URL = import.meta.env.VITE_BASEMAP_URL
  ?? "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

// Cool → hot ramp. ONLY for real opportunity scores. Visual ranking only —
// not a legal eligibility claim. Null/municipal parcels get GREY, never the
// low end of this ramp (G7 honesty: can't conflate "low score" with "no score").
const RAMP = [
  0.01, "#2d4a7a",   // start at >0 because filter excludes 0
  20,   "#3a6fb8",
  40,   "#3aa66f",
  60,   "#e3c83a",
  80,   "#ed8a2e",
  100,  "#ed1f1f",
] as const;

const NEUTRAL_GREY = "#3a3a3a";   // "no score" — visually distinct from ramp
const NEUTRAL_BORDER = "#222";

// Register pmtiles:// protocol once at module load.
const pm = new Protocol();
maplibregl.addProtocol("pmtiles", pm.tile);

export type ParcelClick = { folio: string; props: Record<string, any> };

export function MapView({ onSelect }: { onSelect: (sel: ParcelClick) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: BASEMAP_URL,
      // Center on the unincorporated MD scoring hotspot belt (West Kendall / S Dade)
      center: [-80.38, 25.69],
      zoom: 11.0,
      minZoom: 9,
      maxZoom: 20,
      attributionControl: { compact: true },
    });

    map.on("load", () => {
      // Priority: PMTiles (R2 production) > Martin tiles > static seed GeoJSON.
      const hasPmtiles = !!PMTILES_URL;
      const hasMartin  = !!TILES_URL;

      if (hasPmtiles) {
        map.addSource("parcel_scores", {
          type: "vector",
          url: `pmtiles://${PMTILES_URL}`,
        });
      } else if (hasMartin) {
        map.addSource("parcel_scores", {
          type: "vector",
          tiles: [`${TILES_URL}/parcel_scores/{z}/{x}/{y}`],
          minzoom: 9, maxzoom: 18,
        });
      } else {
        map.addSource("parcel_scores", { type: "geojson", data: "/sample/parcels.geojson" });
      }

      const isVector = hasPmtiles || hasMartin;
      const sourceLayer = hasPmtiles ? "parcels" : (hasMartin ? "parcel_scores" : undefined);

      // Baseline: every parcel as a NEUTRAL GREY tile — clearly "no score yet."
      // Visually distinct from any colour on the ramp.
      const baseline: any = {
        id: "parcels-baseline",
        type: "fill",
        source: "parcel_scores",
        paint: {
          "fill-color": NEUTRAL_GREY,
          "fill-opacity": 0.62,
          "fill-outline-color": NEUTRAL_BORDER,
        },
      };
      if (isVector) baseline["source-layer"] = sourceLayer;
      map.addLayer(baseline);

      // Heat layer: only score > 0 renders in color. Hotspots POP; the rest is muted context.
      const fill: any = {
        id: "parcels-fill",
        type: "fill",
        source: "parcel_scores",
        filter: [">", ["coalesce", ["get", "opportunity_score"], 0], 0],
        paint: {
          "fill-color": ["interpolate", ["linear"], ["get", "opportunity_score"], ...RAMP],
          "fill-opacity": [
            "interpolate", ["linear"], ["get", "opportunity_score"],
            0.1, 0.68,
            30,  0.80,
            60,  0.90,
            100, 0.97,
          ],
          "fill-antialias": true,
        },
      };
      if (isVector) fill["source-layer"] = sourceLayer;
      map.addLayer(fill);

      const hover: any = {
        id: "parcels-hover",
        type: "line",
        source: "parcel_scores",
        paint: { "line-color": "#ffffff", "line-width": 2 },
        filter: ["==", ["get", "folio"], "__none__"],
      };
      if (isVector) hover["source-layer"] = sourceLayer;
      map.addLayer(hover);

      // Click handler — pulls props directly from the PMTiles feature (no extra fetch).
      // Works on both fill layers (heat + baseline).
      const handleClick = (e: any) => {
        const feat = e.features?.[0];
        const f = feat?.properties?.folio;
        if (typeof f === "string") {
          onSelect({ folio: f, props: feat.properties });
          map.setFilter("parcels-hover", ["==", ["get", "folio"], f]);
        }
      };
      map.on("click", "parcels-fill",     handleClick);
      map.on("click", "parcels-baseline", handleClick);
      const cursorOn  = () => (map.getCanvas().style.cursor = "pointer");
      const cursorOff = () => (map.getCanvas().style.cursor = "");
      map.on("mouseenter", "parcels-fill",     cursorOn);
      map.on("mouseleave", "parcels-fill",     cursorOff);
      map.on("mouseenter", "parcels-baseline", cursorOn);
      map.on("mouseleave", "parcels-baseline", cursorOff);
    });

    return () => map.remove();
  }, [onSelect]);

  return <div ref={ref} className="ps-map" />;
}
